import { defineStore } from 'pinia'
import request, { getTenantId } from '@/api/request'

export interface EntitlementQuota {
  limit: number
  used: number
  remaining: number
  unlimited: boolean
  overage: number
  status: 'normal' | 'exhausted' | 'over_quota'
  [key: string]: unknown
}

export interface OverQuotaResource {
  resource: string
  used: number
  limit: number
  overage: number
}

export interface NurturePolicyLimits {
  browse_duration_min: number
  browse_video_max: number
  like_max: number
  comment_max: number
  follow_max: number
  favorite_max: number
  profile_visit_max: number
  open_comments_max: number
  time_windows_max: number
  content_tags_max: number
  custom_keywords_max: number
  comment_templates_max: number
}

export interface NurturePolicy {
  policy_version: number
  plan_id: string
  plan_name: string
  plan_status: 'active' | 'expired' | 'fallback_free'
  expires_at: string | null
  strategy_limit: number
  can_execute: boolean
  limits: NurturePolicyLimits
}

export interface EntitlementSnapshot {
  plan_id: string
  plan_name: string
  status: 'active' | 'expired' | 'fallback_free'
  expires_at: string | null
  features: Record<string, boolean | number | string>
  quotas: Record<string, EntitlementQuota>
  over_quota: OverQuotaResource[]
  nurture_policy: NurturePolicy
}

let activeLoad: Promise<EntitlementSnapshot> | null = null

export const useEntitlementsStore = defineStore('entitlements', {
  state: () => ({
    snapshot: null as EntitlementSnapshot | null,
    loading: false,
    loaded: false,
    loadedTenantId: '',
  }),

  getters: {
    environmentQuota: (state): EntitlementQuota | null => state.snapshot?.quotas?.environments || null,
    nurturePolicy: (state): NurturePolicy | null => state.snapshot?.nurture_policy || null,
    canExecuteNurture: (state): boolean => state.snapshot?.nurture_policy?.can_execute === true,
  },

  actions: {
    async load(force = false) {
      const tenantId = getTenantId()
      if (activeLoad) return activeLoad
      if (this.loaded && !force && this.snapshot && this.loadedTenantId === tenantId) return this.snapshot
      this.loading = true
      try {
        activeLoad = request.get('/billing/entitlements') as Promise<EntitlementSnapshot>
        this.snapshot = await activeLoad
        this.loaded = true
        this.loadedTenantId = tenantId
        return this.snapshot
      } finally {
        activeLoad = null
        this.loading = false
      }
    },

    hasFeature(key: string) {
      return this.snapshot?.features?.[key] === true
    },

    reset() {
      this.snapshot = null
      this.loaded = false
      this.loading = false
      this.loadedTenantId = ''
    },
  },
})
